import { Main } from 'styles/themeColors';
import Order from 'data/Order';
import { navigate } from 'hooks/useHookRouterCompat';
import { DOW_JONES_REVIEWED_BLOCKED, DOW_JONES_BLOCKED } from 'constants/dowJonesStatuses';
import { SHIPPING } from 'constants/deliveryModes';
import {
  CHECKING_CREDIT,
  ORDER_SENT_TO_OEM,
  READY,
  CANCELING,
  CANCELED,
  ON_HOLD,
  PROCESSING,
  MANUFACTURING,
  PACKING,
  SHIPPED_READY_FOR_PICKUP,
  READY_TO_SHIP,
  READY_FOR_PICKUP,
  DELIVERED_PICKED_UP,
  DELAYED,
  SHIPPED,
  PICKED_UP
} from 'constants/consignmentStates';
import { CONSIGNMENT_TYPES } from 'constants/order';
import { PURCHASE_ORDER } from 'constants/paymentTypes';
import { SALES_ORDER_TYPES, SalesOrderType, SALES_ORDER_API_STATUSES } from 'constants/salesOrderTypes';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { ConsignmentDetails } from 'data/Consignment';
import FeatureFlag from 'data/FeatureFlag';
import { SalesOrder } from 'data/SalesOrder';
import { CREDIT_SOURCES } from 'constants/credit';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';

interface DowJonesBannerContent {
  theme: string;
  header: string;
  labels: string;
  button?: { value: string, onClick: () => void };
};

export const retrieveOrderDowJonesBannerContent = (order: Order): DowJonesBannerContent => {
  const theme = (order.dowJones?.status === DOW_JONES_REVIEWED_BLOCKED)
    ? Main.error
    : Main.warning;

  const header = (order.dowJones?.status === DOW_JONES_REVIEWED_BLOCKED)
    ? 'This customer has failed OFMP compliance check'
    : 'The customer has failed the Dow Jones compliance check and requires a review';

  const labels = (order.dowJones?.status === DOW_JONES_REVIEWED_BLOCKED)
    ? 'Order is canceled'
    : 'Till then this order will stay on hold';

  const button = (order.dowJones?.status === DOW_JONES_REVIEWED_BLOCKED)
    ? undefined
    : { value: 'Review customer\'s compliance status', onClick: () => navigate(`/customers/customer-details/${order.customer.id}`, false, { activeTab: 1 }) };

  return { theme, header, labels, button };
};

export const retrieveOrderComponentTitle = (order: Order): string => {
  const initial = 'Split order';
  const status = order.consignments.find(cons => cons)?.state;

  if (!status || !(order.consignments.every(consignment => consignment.state === status)))
    return initial;

  const deliveryTypes = order.consignments.map(consignment => consignment.deliveryType);

  switch (status) {
    case CHECKING_CREDIT: return 'Checking Credit';
    case READY:
    case ORDER_SENT_TO_OEM: return order.dowJones?.status === DOW_JONES_BLOCKED ? 'Compliance Fail' : 'New';
    case CANCELING:
    case CANCELED: return 'Cancelled';
    case ON_HOLD: return 'On Hold';
    case PROCESSING: return 'Processing';
    case MANUFACTURING: return 'Manufacturing';
    case PACKING: return 'Preparing';
    case DELAYED: return 'Delayed';
    case SHIPPED_READY_FOR_PICKUP:
      return (deliveryTypes.length > 1)
        ? 'Shipped / Ready for Pickup'
        : deliveryTypes[0] === SHIPPING
          ? 'Ready to ship'
          : 'Ready for Pickup';
    case READY_TO_SHIP: return 'Ready to ship';
    case READY_FOR_PICKUP: return 'Ready for Pickup';
    case DELIVERED_PICKED_UP:
      return (deliveryTypes.length > 1)
        ? 'Shipped / Ready for Pickup'
        : deliveryTypes[0] === SHIPPING
          ? 'Shipped'
          : 'Picked up';
    case SHIPPED: return 'Shipped';
    case PICKED_UP: return 'Picked up';

    default: return initial;
  }
};

export const shouldUseCreditCheckV2 = (creditCheckFlag: boolean, paymentType: string) => {
  return creditCheckFlag &&
    paymentType === PURCHASE_ORDER;
};

export const getSalesOrderType = (consignmentEntries: ConsignmentEntry[], isManualConfirmed: boolean, isStockManual: boolean, creditSource: string | undefined, orderType: string, salesOrderNumber: string | undefined): SalesOrderType => {
  // Check if ALL entries have brandVendor.isSalesOrderApiEnabled === true
  const allApiEnabled = salesOrderNumber || consignmentEntries.every(entry =>
    entry.brandVendor?.isSalesOrderApiEnabled === true
  );
  return allApiEnabled && !isManualConfirmed && (orderType === PURCHASE_ORDER ? creditSource === CREDIT_SOURCES.MDM : true) && !isStockManual ? SALES_ORDER_TYPES.API : SALES_ORDER_TYPES.MANUAL;
};
export const shouldShowCancellationApprovalModal = (
  consignmentDetails: ConsignmentDetails,
  featureFlags: FeatureFlag
): boolean => {
  return consignmentDetails?.consignmentType === CONSIGNMENT_TYPES.PURCHASE &&
    featureFlags?.salesOrder?.enabled &&
    consignmentDetails?.salesOrderStatus?.status === SALES_ORDER_API_STATUSES.SUCCESS &&
    [PACKING, READY_TO_SHIP, READY_FOR_PICKUP, DELAYED, MANUFACTURING].includes(consignmentDetails.state);
};
export const shouldShowAutomaticCancellation = (
  salesOrderResult: SalesOrder[]
): boolean => {
  if (!Array.isArray(salesOrderResult) || salesOrderResult.length === 0) {
    return false;
  }

  const NOT_YET_PROCESSED = 'Not yet Processed';

  return salesOrderResult.every(entry =>
    entry?.orderRejectionStatus === NOT_YET_PROCESSED &&
    entry?.deliveryStatus === NOT_YET_PROCESSED
  );
};

interface ColumnVisibilityParams {
  columnField: string;
  isAdmin: boolean;
  isVendorUser: boolean;
  consignmentType?: string;
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
}

/**
 * Determines whether a column should be visible based on user permissions,
 * consignment type, and selected filters.
 */
export const shouldShowColumn = (params: ColumnVisibilityParams): boolean => {
  const {
    columnField,
    isAdmin,
    isVendorUser,
    consignmentType,
    selectedVendor,
    selectedBusinessUnit
  } = params;

  // Handle vendor column visibility
  if (columnField === 'vendor') {
    // Only show vendor column for admin users
    return isAdmin;
  }

  // Handle business unit column visibility
  if (columnField === 'businessUnit') {
    // Only show for affiliate orders
    if (consignmentType !== CONSIGNMENT_TYPES.AFFILIATE_PROGRAM) return false;

    // Always show for admin users
    if (isAdmin) return true;

    // For non-admin users, check vendor and business unit selection
    const hasVendor = selectedVendor !== undefined && selectedVendor !== null;
    const hasBusinessUnit = selectedBusinessUnit !== undefined && selectedBusinessUnit !== null;

    // Show business unit if vendor is selected but no business unit is selected
    if (hasVendor && !hasBusinessUnit) return true;

    // Don't show if both are selected
    if (hasVendor && hasBusinessUnit) return false;

    // Show if neither is selected
    if (!hasVendor && !hasBusinessUnit) return true;

    return false; // Hide in other cases
  }

  // Hide warehouse column for affiliate orders
  if (columnField === 'warehouse' && consignmentType === CONSIGNMENT_TYPES.AFFILIATE_PROGRAM) {
    return false;
  }

  // Hide delivery type for affiliate orders
  if (columnField === 'deliveryType' && consignmentType === CONSIGNMENT_TYPES.AFFILIATE_PROGRAM) {
    return false;
  }

  // Subscription-specific hidden fields
  if (consignmentType === CONSIGNMENT_TYPES.SUBSCRIPTION) {
    const subscriptionHiddenFields = ['warehouse', 'deliveryType'];
    if (subscriptionHiddenFields.includes(columnField)) return false;
  }

  return true;
};

